import React, { useEffect, useMemo, useRef, useState } from "react";
import { API } from "@/api";
import axios from "axios";
import {
  ArrowRight, Check, MessageCircle, Sparkles, Zap, ShieldCheck, BarChart3,
  Smartphone, Globe, Wrench, ShoppingCart, Package, Wallet, Users, Lock,
  Rocket, ChevronDown, Star, Layers, TrendingUp, FileText, ScanLine, Eye,
  Building2, Phone, Receipt, Repeat, TrendingDown, Image as ImageIcon,
  CalendarClock, Download,
} from "lucide-react";

// ─────────────────────────────────────────────────────────────────────────────
// Track helper — envia eventos para o backend (sem cookies, sem libs externas).
// Falha silenciosa: se o tracking quebrar, a página continua funcionando.
// ─────────────────────────────────────────────────────────────────────────────
const trackEvent = (type, label) => {
  try {
    const w = window.innerWidth;
    const device = w < 640 ? "mobile" : w < 1024 ? "tablet" : "desktop";
    axios.post(`${API}/public/landing-track`, {
      type, label,
      device,
      referrer: document.referrer || "",
    }).catch(() => {});
  } catch {}
};

const useReveal = () => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || visible) return;
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) { setVisible(true); obs.disconnect(); }
    }, { threshold: 0.12, rootMargin: "0px 0px -60px 0px" });
    obs.observe(el);
    return () => obs.disconnect();
  }, [visible]);
  return [ref, visible];
};

const Reveal = ({ children, delay = 0, className = "" }) => {
  const [ref, visible] = useReveal();
  return (
    <div
      ref={ref}
      style={{ transitionDelay: `${delay}ms` }}
      className={`transition-all duration-700 ease-out ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"} ${className}`}
    >
      {children}
    </div>
  );
};

const MODULES = [
  { icon: ShoppingCart, name: "PDV Inteligente", desc: "Venda com leitor de código, descontos por item e produto avulso — em segundos. Suporta pagamentos combinados (Pix + Dinheiro + Cartão na mesma venda) e taxas extras." },
  { icon: Repeat, name: "Crediário", desc: "Parcelas mensais, quinzenais ou semanais. Botão 'Cobrar' envia link de pagamento Pix direto no WhatsApp do cliente. Agenda mostra vencidas, vence hoje e a vencer." },
  { icon: Users, name: "CRM com Kanban", desc: "Funil completo: do primeiro contato ao pós-venda. Renomeie etapas, agende próximo contato, registre observações fixadas e acompanhe lucro por lead." },
  { icon: Wrench, name: "Assistência Técnica", desc: "OS com itens ilimitados, termos personalizados, fotos, estado físico do produto, defeito/solução, status público com link de rastreio e PDF compacto." },
  { icon: Package, name: "Pedidos & Kanban", desc: "Acompanhe cada pedido em colunas: Pendente, Separando, Aguardando, Pago, Entregue. Pix gerado automaticamente." },
  { icon: Globe, name: "Catálogo Online", desc: "Sua vitrine própria com link único. Cliente monta o pedido e finaliza direto no WhatsApp da loja." },
  { icon: ImageIcon, name: "Modo Stories", desc: "Gera uma imagem 9:16 pronta pra Instagram Stories direto do produto: foto, nome, descrição, valor e marca da loja. Postou em 5 segundos." },
  { icon: BarChart3, name: "Financeiro Detalhado", desc: "Lucro por venda, OS e pedido pago. Filtros multi-categoria por dia, semana, mês ou personalizado. Inclui despesas no lucro final." },
  { icon: TrendingDown, name: "Controle de Despesas", desc: "Despesas fixas mensais (Aluguel, Internet, Salários) e avulsas. Alertas de vencimento, status pago/pendente/atrasada, gráficos comparativos." },
  { icon: FileText, name: "Relatórios PDF + Excel", desc: "Exporte produtos, financeiro e despesas em PDF profissional ou planilha Excel. Filtros completos: período, categoria, tipo, com/sem estoque." },
  { icon: Receipt, name: "Impressão Térmica 58/80mm", desc: "Recibo no padrão do supermercado, sem cabeçalho do navegador. Funciona em qualquer impressora térmica USB ou Bluetooth." },
  { icon: ScanLine, name: "Leitor de Códigos", desc: "Câmera do celular ou desktop lendo EAN-13, QR e mais. Múltiplos códigos por produto." },
  { icon: Wallet, name: "Conta Corrente do Cliente", desc: "Crédito e débito por cliente, parcelamento informal e fechamento por extrato — sem planilha." },
  { icon: Lock, name: "Acessos de Colaboradores", desc: "Lojista, gerente, colaborador, técnico — cada um vê só o que precisa. Sem vazamento de custo. Bloqueio em tempo real." },
];

const BENEFITS = [
  { title: "Ganhe horas todo dia", desc: "Centralize vendas, OS, estoque, financeiro, CRM e despesas num único lugar — adeus 7 abas abertas.", icon: Zap },
  { title: "Vende enquanto você dorme", desc: "Catálogo online + Stories prontos pra postar + crediário com cobrança automática no WhatsApp.", icon: Rocket },
  { title: "Decisões baseadas em números", desc: "Lucro real por venda, OS, pedido E despesas no relatório. Exporte em PDF/Excel quando o contador pedir.", icon: TrendingUp },
  { title: "Equipe sem confusão", desc: "Cada colaborador com seu acesso, sem ver custos ou margens. Bloqueio em tempo real quando precisar.", icon: ShieldCheck },
];

const DIFFERENTIALS = [
  "Sem app pra baixar — abre no navegador do celular, tablet ou PC",
  "Crediário com cobrança automática via WhatsApp + link PIX pronto",
  "Modo Stories: gera imagem 9:16 do produto pronta pra Instagram",
  "Pagamentos combinados na mesma venda (Pix + Dinheiro + Cartão + Troca)",
  "Relatórios profissionais em PDF + Excel com logo da loja",
  "Recibo térmico personalizado (58mm/80mm) sem cabeçalho do navegador",
  "Beep sonoro ao bipar produto, igual leitor de supermercado",
  "Catálogo público com SEO friendly e link curto",
  "Backups automáticos e infraestrutura na nuvem",
  "Suporte humano via WhatsApp, sem ticket frio",
];

const FAQ = [
  { q: "Preciso instalar alguma coisa?", a: "Não. Funciona 100% no navegador — celular, tablet, PC ou totem. Basta logar." },
  { q: "Tem crediário/parcelamento?", a: "Sim. Crediário completo com parcelas mensais, quinzenais ou semanais. O sistema gera link PIX por parcela e o botão 'Cobrar' envia tudo no WhatsApp do cliente automaticamente." },
  { q: "Como funciona o Modo Stories?", a: "Em qualquer produto cadastrado, basta clicar em 'Compartilhar' → 'Stories'. O sistema gera uma imagem 9:16 com a foto do produto, nome, descrição, valor e marca da loja — pronta pra postar no Instagram." },
  { q: "Consigo gerar relatórios pro contador?", a: "Sim. Em Produtos, Financeiro e Despesas tem o botão 'Exportar relatório' que gera PDF profissional ou planilha Excel com seu logo, totais e filtros personalizados." },
  { q: "Funciona offline?", a: "O sistema precisa de internet pra registrar vendas, mas usa cache inteligente — quedas curtas não derrubam o caixa." },
  { q: "Posso testar antes de pagar?", a: "Sim. Oferecemos teste grátis para você sentir o sistema na real, sem cartão de crédito." },
  { q: "Quantos colaboradores posso cadastrar?", a: "Depende do plano. A partir do Pro, são ilimitados — com permissões individuais por função." },
  { q: "Meus dados ficam seguros?", a: "Sim. Conexão criptografada, banco em nuvem, backups diários e isolamento total entre lojas (multi-tenant)." },
  { q: "Funciona pra qualquer tipo de loja?", a: "Sim. Roupas, pesca, eletrônicos, suplementos, assistência técnica, beleza, papelaria — qualquer comércio que faz venda + atende cliente." },
];

export default function Landing() {
  const [cfg, setCfg] = useState(null);
  const [openFaq, setOpenFaq] = useState(0);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    axios.get(`${API}/public/landing-config`).then(r => setCfg(r.data)).catch(() => setCfg({}));
    trackEvent("visit");
  }, []);

  // Atualiza SEO meta tags quando a config carrega
  useEffect(() => {
    if (!cfg) return;
    if (cfg.seo_title) document.title = cfg.seo_title;
    let m = document.querySelector('meta[name="description"]');
    if (!m) { m = document.createElement("meta"); m.name = "description"; document.head.appendChild(m); }
    m.content = cfg.seo_description || "";
  }, [cfg]);

  const sec = cfg?.sections || {};
  const enabled = cfg?.enabled !== false;
  const primary = cfg?.primary_color || "#10B981";
  const accent = cfg?.accent_color || "#0EA5E9";

  const whatsappHref = useMemo(() => {
    if (!cfg?.whatsapp_number) return "#";
    const num = String(cfg.whatsapp_number).replace(/\D/g, "");
    const msg = encodeURIComponent(cfg.whatsapp_message || "");
    return `https://wa.me/${num}?text=${msg}`;
  }, [cfg]);

  const goWhatsapp = (label) => () => {
    trackEvent("click_whatsapp", label);
    window.open(whatsappHref, "_blank", "noopener,noreferrer");
  };
  // Navega para URL: externas (http://...) abrem em nova aba; relativas (/login)
  // continuam no mesmo tab. Mantém UX consistente entre o domínio do landing
  // e o painel principal.
  const navigateTo = (url) => {
    let u = String(url || "").trim();
    if (!u) return;
    // Se começa com `/` é rota interna. Caso contrário (ex.: "gestaoexpert.app.br"
    // ou "https://..."), tratamos como externo e adicionamos https:// se faltar.
    const isInternal = u.startsWith("/");
    if (!isInternal && !/^https?:\/\//i.test(u)) {
      u = "https://" + u;
    }
    if (isInternal) {
      window.location.href = u;
    } else {
      window.open(u, "_blank", "noopener,noreferrer");
    }
  };
  const goTrial = (label) => () => {
    trackEvent("click_trial", label);
    navigateTo(cfg?.trial_url || "/login");
  };
  const goLogin = (label) => () => {
    trackEvent("click_login", label);
    navigateTo(cfg?.login_url || "https://gestaoexpert.app.br");
  };
  const goBuy = (label) => () => {
    trackEvent("click_buy", label);
    if (cfg?.buy_url) window.open(cfg.buy_url, "_blank", "noopener,noreferrer");
    else goWhatsapp(label)();
  };
  const scrollTo = (id) => () => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    trackEvent("click_other", `scroll_${id}`);
  };

  // Se a landing está desativada pelo master, mostra um aviso suave
  if (cfg && !enabled) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#050810] text-white p-6">
        <div className="text-center max-w-md">
          <h1 className="font-display text-3xl mb-2">{cfg.brand_name || "Gestão Expert"}</h1>
          <p className="text-neutral-400">Esta página está temporariamente indisponível. Entre em contato pelos nossos canais oficiais.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#050810] text-white min-h-screen overflow-x-hidden font-sans" data-testid="landing-page">
      <style>{`
        :root { --ge-primary: ${primary}; --ge-accent: ${accent}; }
        .grad-text { background: linear-gradient(135deg, var(--ge-primary), var(--ge-accent)); -webkit-background-clip: text; background-clip: text; color: transparent; }
        .grad-btn { background: linear-gradient(135deg, var(--ge-primary), var(--ge-accent)); }
        .grad-btn:hover { filter: brightness(1.1); transform: translateY(-1px); }
        .ge-grid-bg { background-image: radial-gradient(circle at 20% 10%, rgba(16,185,129,0.18), transparent 50%), radial-gradient(circle at 85% 50%, rgba(14,165,233,0.18), transparent 55%); }
        .ge-grain { background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='200' height='200'><filter id='n'><feTurbulence baseFrequency='0.9'/></filter><rect width='200' height='200' filter='url(%23n)' opacity='0.06'/></svg>"); }
      `}</style>

      {/* NAVBAR */}
      <header className="fixed top-0 left-0 right-0 z-40 backdrop-blur bg-[#050810]/60 border-b border-white/5">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
          <a href="#top" onClick={scrollTo("top")} className="flex items-center gap-2 font-display text-base tracking-tight">
            <span className="w-7 h-7 rounded-md grad-btn grid place-items-center text-[10px] font-bold">GE</span>
            <span>{cfg?.brand_name || "Gestão Expert"}</span>
          </a>
          <nav className="hidden md:flex items-center gap-7 text-sm text-neutral-300">
            <button onClick={scrollTo("modulos")} className="hover:text-white transition">Módulos</button>
            <button onClick={scrollTo("beneficios")} className="hover:text-white transition">Benefícios</button>
            <button onClick={scrollTo("diferenciais")} className="hover:text-white transition">Diferenciais</button>
            <button onClick={scrollTo("faq")} className="hover:text-white transition">Dúvidas</button>
          </nav>
          <div className="flex items-center gap-2">
            <button onClick={goLogin("navbar")} className="text-xs px-4 py-2 rounded-md grad-btn font-semibold transition" data-testid="cta-login-nav">
              Já é usuário? Faça login
            </button>
          </div>
        </div>
      </header>

      <div id="top"/>

      {/* HERO */}
      {sec.hero !== false && (
        <section className="relative pt-32 pb-20 sm:pt-40 sm:pb-28 ge-grid-bg">
          <div className="absolute inset-0 ge-grain pointer-events-none"/>
          <div className="max-w-6xl mx-auto px-4 sm:px-6 relative">
            <Reveal>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-white/10 bg-white/5 text-[11px] uppercase tracking-widest text-neutral-300 mb-7">
                <Sparkles size={12} className="text-emerald-400"/> Plataforma all-in-one
              </div>
            </Reveal>
            <Reveal delay={80}>
              <h1 className="font-display text-4xl sm:text-6xl lg:text-7xl leading-[1.05] tracking-tight max-w-4xl">
                {cfg?.hero_title || "A gestão que sua empresa precisava — sem a complexidade que ela não merece."}
              </h1>
            </Reveal>
            <Reveal delay={150}>
              <p className="mt-6 text-base sm:text-lg text-neutral-300 max-w-2xl leading-relaxed">
                {cfg?.hero_subtitle}
              </p>
            </Reveal>
            <Reveal delay={220}>
              <div className="mt-9 flex flex-wrap gap-3">
                <button onClick={goTrial("hero")} className="grad-btn px-6 h-12 rounded-md text-sm font-semibold inline-flex items-center gap-2 transition" data-testid="cta-trial-hero">
                  <Rocket size={16}/> {cfg?.hero_cta_primary || "Faça um teste grátis"}
                </button>
                <button onClick={scrollTo("modulos")} className="px-6 h-12 rounded-md text-sm font-medium border border-white/15 hover:border-white/40 inline-flex items-center gap-2 transition" data-testid="cta-saiba-mais-hero">
                  {cfg?.hero_cta_secondary || "Saiba mais"} <ArrowRight size={14}/>
                </button>
                <button onClick={goWhatsapp("hero")} className="px-6 h-12 rounded-md text-sm font-medium bg-emerald-600 hover:bg-emerald-500 inline-flex items-center gap-2 transition" data-testid="cta-whatsapp-hero">
                  <MessageCircle size={16}/> Falar no WhatsApp
                </button>
              </div>
            </Reveal>
            <Reveal delay={300}>
              <div className="mt-12 grid sm:grid-cols-3 gap-6 max-w-3xl">
                {[
                  { k: "+10 módulos", v: "Vendas, OS, catálogo, financeiro" },
                  { k: "100% web", v: "Sem instalar nada — abre em qualquer aparelho" },
                  { k: "Multi-loja", v: "Cada filial com seu painel próprio" },
                ].map((s, i) => (
                  <div key={i} className="border-l border-white/10 pl-4">
                    <div className="text-xl font-display">{s.k}</div>
                    <div className="text-xs text-neutral-400 mt-1">{s.v}</div>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </section>
      )}

      {/* MÓDULOS */}
      {sec.modules !== false && (
        <section id="modulos" className="py-24 border-t border-white/5">
          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            <Reveal>
              <p className="text-xs uppercase tracking-[0.2em] text-emerald-400 mb-3">O que você ganha</p>
              <h2 className="font-display text-3xl sm:text-5xl tracking-tight max-w-3xl">Tudo o que sua operação precisa em <span className="grad-text">um painel só</span>.</h2>
              <p className="mt-4 text-neutral-400 max-w-2xl">Pare de pular entre app de caixa, planilha de OS, calculadora e bloquinho de fiado. O Gestão Expert é a operação completa, conectada de ponta a ponta.</p>
            </Reveal>
            <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {MODULES.map((m, i) => (
                <Reveal key={m.name} delay={i * 60}>
                  <div className="group relative h-full p-6 rounded-2xl bg-white/5 border border-white/10 hover:border-white/30 transition overflow-hidden" data-testid={`module-card-${i}`}>
                    <div className="absolute -inset-px rounded-2xl opacity-0 group-hover:opacity-100 transition pointer-events-none" style={{ background: "linear-gradient(135deg, rgba(16,185,129,0.15), rgba(14,165,233,0.1))" }}/>
                    <m.icon size={22} className="relative text-emerald-400 mb-4"/>
                    <h3 className="relative font-display text-lg leading-tight">{m.name}</h3>
                    <p className="relative mt-2 text-sm text-neutral-400 leading-relaxed">{m.desc}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* BENEFÍCIOS — split layout */}
      {sec.benefits !== false && (
        <section id="beneficios" className="py-24 border-t border-white/5 ge-grid-bg">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 grid lg:grid-cols-2 gap-14 items-center">
            <Reveal>
              <p className="text-xs uppercase tracking-[0.2em] text-emerald-400 mb-3">Por que muda o jogo</p>
              <h2 className="font-display text-3xl sm:text-5xl tracking-tight">Mais lucro, menos retrabalho.</h2>
              <p className="mt-5 text-neutral-300 leading-relaxed">
                Você não precisa de mais um sistema. Você precisa de um que <em>conversa entre si</em>: o caixa abate do estoque, a OS gera recibo, o pedido vira pagamento PIX, o financeiro mostra quanto sobrou — sem importar planilha, sem revisão manual, sem dúvida no fim do mês.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <button onClick={goTrial("benefits")} className="grad-btn px-5 h-11 rounded-md text-sm font-semibold inline-flex items-center gap-2 transition" data-testid="cta-trial-beneficios">
                  Quero testar grátis <ArrowRight size={14}/>
                </button>
                <button onClick={goWhatsapp("benefits")} className="px-5 h-11 rounded-md text-sm font-medium border border-white/15 hover:border-white/40 inline-flex items-center gap-2 transition">
                  <MessageCircle size={14}/> Tirar dúvidas
                </button>
              </div>
            </Reveal>

            <div className="grid sm:grid-cols-2 gap-3">
              {BENEFITS.map((b, i) => (
                <Reveal key={b.title} delay={i * 80}>
                  <div className="h-full p-5 rounded-xl bg-white/5 border border-white/10 hover:bg-white/[0.07] transition">
                    <b.icon size={18} className="text-emerald-400 mb-3"/>
                    <h3 className="font-display text-base">{b.title}</h3>
                    <p className="mt-2 text-sm text-neutral-400 leading-relaxed">{b.desc}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FEATURES detalhe — alternando */}
      {sec.features !== false && (
        <section className="py-24 border-t border-white/5">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 space-y-24">
            {[
              {
                tag: "Catálogo + WhatsApp",
                title: "Sua loja vende online — sem marketplace, sem comissão.",
                desc: "Cada loja ganha um link público do tipo gestaoexpert.app.br/catalogo/sua-loja. O cliente escolhe produtos, monta o carrinho e finaliza no WhatsApp da sua equipe. O pedido entra direto no kanban interno com o link PIX gerado.",
                bullets: ["Filtros por categoria e busca", "Variações (cor, tamanho)", "Pedido com link de pagamento incluso", "Status público acompanhável pelo cliente"],
                icon: Globe,
              },
              {
                tag: "Assistência Técnica",
                title: "OS organizada, rastreável e profissional.",
                desc: "Cadastre o equipamento, anexe fotos, registre o defeito, gere a OS com termos personalizados. O cliente acompanha cada passo num link público — sem te ligar a cada 2h perguntando 'tá pronto?'.",
                bullets: ["Itens ilimitados na OS", "Termos com fonte ajustável (6 tamanhos)", "PDF/imagem compacta pra imprimir", "Link público de rastreio por OS"],
                icon: Wrench,
              },
              {
                tag: "Financeiro de verdade",
                title: "Veja o lucro real, no dia que entrou.",
                desc: "Vendas direto do PDV, pedidos pagos no kanban e OS finalizadas viram lucro contabilizado no dia da confirmação — não no dia do orçamento. Filtros Este Mês / Semana / 15 dias / Anual / Total já vêm prontos.",
                bullets: ["Lucro por canal: PDV, Pedido, OS", "Filtros respeitando fuso BRT", "Preferência salva entre sessões", "Permissão restrita: colaborador não vê custo"],
                icon: BarChart3,
              },
            ].map((f, i) => (
              <Reveal key={f.tag}>
                <div className={`grid lg:grid-cols-2 gap-12 items-center ${i % 2 === 1 ? "lg:[&>*:first-child]:order-2" : ""}`}>
                  <div>
                    <span className="inline-block text-[10px] uppercase tracking-[0.25em] text-emerald-400 mb-3">{f.tag}</span>
                    <h3 className="font-display text-2xl sm:text-4xl leading-tight tracking-tight">{f.title}</h3>
                    <p className="mt-4 text-neutral-400 leading-relaxed">{f.desc}</p>
                    <ul className="mt-6 space-y-2">
                      {f.bullets.map(b => (
                        <li key={b} className="flex items-start gap-2 text-sm text-neutral-300">
                          <Check size={14} className="text-emerald-400 mt-0.5 shrink-0"/> {b}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="relative aspect-[5/4] rounded-2xl border border-white/10 bg-gradient-to-br from-white/[0.04] to-white/[0.01] overflow-hidden p-8 flex items-center justify-center">
                    <div className="absolute inset-0 ge-grid-bg opacity-60"/>
                    <f.icon size={120} className="relative text-emerald-300/70" strokeWidth={1}/>
                    <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between text-[10px] text-neutral-400 uppercase tracking-widest">
                      <span>{f.tag}</span>
                      <span className="flex items-center gap-1"><Eye size={10}/> ao vivo</span>
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </section>
      )}

      {/* DIFERENCIAIS */}
      {sec.differentials !== false && (
        <section id="diferenciais" className="py-24 border-t border-white/5">
          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            <Reveal>
              <p className="text-xs uppercase tracking-[0.2em] text-emerald-400 mb-3">O que ninguém mais entrega</p>
              <h2 className="font-display text-3xl sm:text-5xl tracking-tight max-w-3xl">Detalhes que <span className="grad-text">só quem opera no chão da loja</span> percebe.</h2>
            </Reveal>
            <div className="mt-12 grid sm:grid-cols-2 gap-3">
              {DIFFERENTIALS.map((d, i) => (
                <Reveal key={d} delay={i * 40}>
                  <div className="flex items-start gap-3 p-4 rounded-lg bg-white/[0.03] border border-white/5">
                    <span className="grad-btn w-6 h-6 rounded-full grid place-items-center shrink-0 mt-0.5">
                      <Check size={12} className="text-white" strokeWidth={3}/>
                    </span>
                    <span className="text-sm text-neutral-200 leading-relaxed">{d}</span>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA PRICING */}
      {sec.pricing_cta !== false && (
        <section className="py-24 border-t border-white/5">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <Reveal>
              <div className="relative rounded-3xl border border-white/10 overflow-hidden p-10 sm:p-14 text-center bg-gradient-to-br from-white/[0.05] to-white/[0.01]">
                <div className="absolute inset-0 ge-grid-bg opacity-40"/>
                <div className="relative">
                  <p className="text-xs uppercase tracking-[0.25em] text-emerald-400 mb-4">Pronto pra começar?</p>
                  <h2 className="font-display text-3xl sm:text-5xl tracking-tight max-w-2xl mx-auto">Teste agora. Sem cartão, sem amarras.</h2>
                  <p className="mt-5 text-neutral-300 max-w-xl mx-auto">Crie sua loja em 2 minutos, importe seus produtos, faça a primeira venda hoje. Se não amar, é só sair.</p>
                  <div className="mt-9 flex flex-wrap gap-3 justify-center">
                    <button onClick={goTrial("pricing")} className="grad-btn px-7 h-12 rounded-md text-sm font-semibold inline-flex items-center gap-2 transition" data-testid="cta-trial-pricing">
                      <Rocket size={16}/> Começar teste grátis
                    </button>
                    {cfg?.buy_url && (
                      <button onClick={goBuy("pricing")} className="px-7 h-12 rounded-md text-sm font-semibold bg-white !text-neutral-900 hover:bg-neutral-200 inline-flex items-center gap-2 transition" data-testid="cta-buy-pricing">
                        Comprar agora <ArrowRight size={14}/>
                      </button>
                    )}
                    <button onClick={goWhatsapp("pricing")} className="px-7 h-12 rounded-md text-sm font-medium border border-white/15 hover:border-white/40 inline-flex items-center gap-2 transition">
                      <MessageCircle size={14}/> Falar com a gente
                    </button>
                  </div>
                </div>
              </div>
            </Reveal>
          </div>
        </section>
      )}

      {/* FAQ */}
      {sec.faq !== false && (
        <section id="faq" className="py-24 border-t border-white/5">
          <div className="max-w-3xl mx-auto px-4 sm:px-6">
            <Reveal>
              <p className="text-xs uppercase tracking-[0.2em] text-emerald-400 mb-3">Dúvidas frequentes</p>
              <h2 className="font-display text-3xl sm:text-5xl tracking-tight">Tudo o que você quer saber.</h2>
            </Reveal>
            <div className="mt-10 space-y-2">
              {FAQ.map((f, i) => (
                <Reveal key={f.q} delay={i * 40}>
                  <button
                    onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
                    className="w-full text-left rounded-xl bg-white/[0.04] border border-white/10 hover:border-white/20 transition px-5 py-4"
                    data-testid={`faq-${i}`}
                  >
                    <div className="flex items-center justify-between gap-4">
                      <span className="font-medium text-base">{f.q}</span>
                      <ChevronDown size={16} className={`shrink-0 transition-transform text-neutral-400 ${openFaq === i ? "rotate-180" : ""}`}/>
                    </div>
                    {openFaq === i && (
                      <p className="mt-3 text-sm text-neutral-400 leading-relaxed animate-in fade-in slide-in-from-top-1">{f.a}</p>
                    )}
                  </button>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FOOTER */}
      {sec.footer_cta !== false && (
        <footer className="py-16 border-t border-white/5">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 grid sm:grid-cols-3 gap-8">
            <div>
              <div className="flex items-center gap-2 font-display text-base">
                <span className="w-7 h-7 rounded-md grad-btn grid place-items-center text-[10px] font-bold">GE</span>
                {cfg?.brand_name || "Gestão Expert"}
              </div>
              <p className="mt-4 text-sm text-neutral-400 leading-relaxed">{cfg?.tagline}</p>
            </div>
            <div className="text-sm text-neutral-400">
              <p className="text-xs uppercase tracking-widest text-neutral-500 mb-3">Atalhos</p>
              <ul className="space-y-2">
                <li><button onClick={scrollTo("modulos")} className="hover:text-white">Módulos</button></li>
                <li><button onClick={scrollTo("beneficios")} className="hover:text-white">Benefícios</button></li>
                <li><button onClick={scrollTo("faq")} className="hover:text-white">FAQ</button></li>
                <li><button onClick={goLogin("footer")} className="hover:text-white">Já é usuário? Entrar</button></li>
              </ul>
            </div>
            <div>
              <p className="text-xs uppercase tracking-widest text-neutral-500 mb-3">Fale com a gente</p>
              <button onClick={goWhatsapp("footer")} className="inline-flex items-center gap-2 text-sm bg-emerald-600 hover:bg-emerald-500 px-4 h-10 rounded-md font-medium transition">
                <MessageCircle size={14}/> WhatsApp
              </button>
            </div>
          </div>
          <div className="max-w-6xl mx-auto px-4 sm:px-6 mt-12 pt-6 border-t border-white/5 text-xs text-neutral-500 flex flex-wrap items-center justify-between gap-2">
            <span>© {new Date().getFullYear()} {cfg?.brand_name || "Gestão Expert"}. Todos os direitos reservados.</span>
            <span>Construído com obsessão por lojistas reais.</span>
          </div>
        </footer>
      )}

      {/* CTA flutuante WhatsApp — mobile-first */}
      <button
        onClick={goWhatsapp("floating")}
        className="fixed bottom-5 right-5 z-50 bg-emerald-600 hover:bg-emerald-500 rounded-full w-14 h-14 grid place-items-center shadow-2xl shadow-emerald-900/40 transition hover:scale-105"
        aria-label="WhatsApp"
        data-testid="cta-whatsapp-floating"
      >
        <MessageCircle size={22}/>
      </button>
    </div>
  );
}
